import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import LegacyBaseDialog from '../LegacyBaseDialog'
import LegacyFilePartyDropdown, { IFilePartyDropdownOption, mapPartyDtosToDropdownOptions } from '../../Dropdown/LegacyFilePartyDropdown'
import { FilePartyDto, MainDto } from '../../../config/filePartyConfig'
import { ActionButton, DialogFooter, DirectionalHint, IDialogProps, PrimaryButton } from '@fluentui/react'
import IconValue from '../../IconValue/IconValue'
import { getPartyIconName } from '../../../helpers/iconHelper'
import { getDropdownProps } from '../../../helpers/formHelper'
import { IFileDto } from '../../../config/fileConfig'
import { useTranslation } from 'react-i18next'
import EmailAddressDetails = Office.EmailAddressDetails

export type ComposeOutlookItemDialogProps = IDialogProps & {
    file: IFileDto
    parties: MainDto[]
    submitText: string
    onCompose: (recipients: EmailAddressDetails[], subject: string) => Promise<void>
}

export const formatRecipients = (recipients: (MainDto | FilePartyDto)[]): EmailAddressDetails[] =>
    recipients.flatMap(
        (party: MainDto | FilePartyDto) =>
            (party.email || '').split(/[,;]/).map(emailAddress => ({
                displayName: party.displayName,
                emailAddress,
            })) as EmailAddressDetails[],
    )

export const formatSubject = (file: IFileDto, recipients: (MainDto | FilePartyDto)[]): string => {
    const references: string[] = [file.reference, ...recipients.filter(party => !!party.fileReference).map(party => party.fileReference)]
    return `${file.name} ${[...new Set(references)].map(reference => `[${reference}]`).join(' - ')}`
}

export default function ComposeOutlookItemDialog(props: ComposeOutlookItemDialogProps): ReactElement {
    const { t: translate } = useTranslation()

    const [filePartyIds, setFilePartyIds] = useState<string[]>([])
    // Options fill be flattened and easier to query.
    const options = useMemo<IFilePartyDropdownOption<MainDto | FilePartyDto>[]>(
        () => mapPartyDtosToDropdownOptions(props.parties || []),
        [props.parties],
    )

    return (
        <LegacyBaseDialog {...props}>
            <LegacyFilePartyDropdown
                label={translate('email.compose.recipient_other')}
                // Prevent the dialog from overlapping with any action buttons.
                calloutProps={{ directionalHint: DirectionalHint.topAutoEdge, directionalHintFixed: true }}
                options={options}
                onRenderName={(option: IFilePartyDropdownOption<MainDto | FilePartyDto>) => (
                    <IconValue iconName={getPartyIconName(option.data)} value={option.data.displayName} />
                )}
                {...getDropdownProps(filePartyIds, undefined, (values: string[]) => setFilePartyIds(values), true)}
                focusOnMount={true}
            />
            <DialogFooter>
                <PrimaryButton
                    text={props.submitText}
                    onClick={() => {
                        const recipients: (MainDto | FilePartyDto)[] = options
                            .map((party: IFilePartyDropdownOption<MainDto | FilePartyDto>) => party.data)
                            .filter((party: MainDto | FilePartyDto) => filePartyIds.includes(party.pivotId))

                        props.onCompose(formatRecipients(recipients), formatSubject(props.file, recipients)).then(() => setFilePartyIds([]))
                    }}
                />
                <ActionButton text={translate('action.cancel')} onClick={props.onDismiss} />
            </DialogFooter>
        </LegacyBaseDialog>
    )
}
